/*
Copyright (C) 2020  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";

import { useNavigate } from "react-router";

import Modal from "@src/components/ui/Modal";
import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import FilterList from "@src/components/ui/Lists/FilterList";
import PageHeader from "@src/components/smart/PageHeader";

import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

import projectStore from "@src/stores/ProjectStore";

import configLoader from "@src/utils/Config";
import { MinionPool } from "@src/@types/MinionPool";

import providerStore from "@src/stores/ProviderStore";
import endpointStore from "@src/stores/EndpointStore";
import minionPoolStore from "@src/stores/MinionPoolStore";
import { Endpoint } from "@src/@types/Endpoint";
import MinionEndpointModal from "@src/components/modules/MinionModule/MinionEndpointModal";
import MinionPoolModal from "@src/components/modules/MinionModule/MinionPoolModal";
import MinionPoolListItem from "@src/components/modules/MinionModule/MinionPoolListItem";
import { ThemePalette } from "@src/components/Theme";
import AlertModal from "@src/components/ui/AlertModal";
import MinionPoolConfirmationModal from "@src/components/modules/MinionModule/MinionPoolConfirmationModal";
import notificationStore from "@src/stores/NotificationStore";
import { MINION_POOL_COLUMNS } from "@src/components/modules/MinionModule/MinionPoolListItem/MinionPoolListItem";
import { providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import {
  ClearFiltersButton,
  FilterSelect,
  Segmented,
  ToolbarSearch,
  matchesSearch,
  useListQuery,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import { Bot } from "lucide-react";

const Wrapper = styled.div<any>``;

type Props = {
  onNavigate: (path: string) => void;
  query: ListQuery;
};

const STATUS_GROUPS: { [group: string]: string[] } = {
  allocated: ["ALLOCATED", "IN_USE", "AVAILABLE"],
  deallocated: ["DEALLOCATED", "UNINITIALIZED", "INITIALIZED"],
  working: [
    "ALLOCATING",
    "ALLOCATING_MACHINES",
    "ALLOCATING_SHARED_RESOURCES",
    "DEALLOCATING",
    "DEALLOCATING_MACHINES",
    "DEALLOCATING_SHARED_RESOURCES",
    "INITIALIZING",
    "UNINITIALIZING",
    "VALIDATING_INPUTS",
    "RECONFIGURING",
    "RESCALING",
    "SCALING",
    "IN_MAINTENANCE",
  ],
  error: ["ERROR", "ERROR_ALLOCATING_MINIONS"],
};
const FILTER_KEYS = ["q", "status", "provider", "os", "platform"];

type State = {
  modalIsOpen: boolean;
  selectedMinionPools: MinionPool[];
  showChooseMinionEndpointModal: boolean;
  showMinionPoolModal: boolean;
  selectedMinionPoolEndpoint: Endpoint | null;
  showDeletePoolsModal: boolean;
  showDeallocateConfirmation: boolean;
  selectedMinionPoolPlatform: "source" | "destination";
};

@observer
class MinionPoolsPage extends React.Component<Props, State> {
  state: State = {
    modalIsOpen: false,
    selectedMinionPools: [],
    showChooseMinionEndpointModal: false,
    selectedMinionPoolEndpoint: null,
    showMinionPoolModal: false,
    showDeletePoolsModal: false,
    showDeallocateConfirmation: false,
    selectedMinionPoolPlatform: "source",
  };

  pollTimeout = 0;

  stopPolling = false;

  offSwiftRefresh?: () => void;

  componentDidMount() {
    // The top bar's Refresh reloads this page's data.
    this.offSwiftRefresh = onSwiftRefresh(() => {
      this.handleReloadButtonClick();
    });
    document.title = "SwiftIQ Agents · SwiftIQ";

    projectStore.getProjects();
    endpointStore.getEndpoints();
    this.stopPolling = false;
    this.pollData(minionPoolStore.minionPools.length === 0);
  }

  componentWillUnmount() {
    this.offSwiftRefresh?.();
    clearTimeout(this.pollTimeout);
    this.stopPolling = true;
  }

  get filtersActive() {
    return FILTER_KEYS.some(k => this.props.query.get(k));
  }

  clearFilters() {
    this.props.query.set({ q: null, status: null, provider: null, os: null, platform: null });
  }

  filteredPools(except?: string): MinionPool[] {
    const q = this.props.query;
    const text = q.get("q");
    const status = q.get("status");
    const providers = q.getList("provider");
    const os = q.get("os");
    const platform = q.get("platform");
    return minionPoolStore.minionPools.filter(pool => {
      if (except !== "status" && status && !(STATUS_GROUPS[status] || []).includes(pool.status)) return false;
      if (providers.length && !providers.includes(this.getEndpoint(pool.endpoint_id)?.type || "")) return false;
      if (os && pool.os_type !== os) return false;
      if (platform && pool.platform !== platform) return false;
      if (text) {
        return matchesSearch(text, [pool.name, pool.notes, pool.id, this.getEndpoint(pool.endpoint_id)?.name]);
      }
      return true;
    });
  }

  renderToolbar() {
    const q = this.props.query;
    const forStatus = this.filteredPools("status");
    const count = (group: string) => forStatus.filter(p => STATUS_GROUPS[group].includes(p.status)).length;
    const providers = [
      ...new Set(
        minionPoolStore.minionPools
          .map(p => this.getEndpoint(p.endpoint_id)?.type)
          .filter(Boolean) as string[],
      ),
    ]
      .map(t => ({ value: t, label: providerLabel(t) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return (
      <>
        <ToolbarSearch value={q.get("q")} placeholder="Search SwiftIQ Agents" onChange={v => q.set({ q: v })} />
        <Segmented
          label="Status"
          value={q.get("status")}
          onChange={v => q.set({ status: v })}
          options={[
            { value: "", label: "All" },
            { value: "allocated", label: "Allocated", count: count("allocated") },
            { value: "working", label: "In progress", count: count("working") },
            { value: "deallocated", label: "Deallocated", count: count("deallocated") },
            { value: "error", label: "Error", count: count("error") },
          ]}
        />
        <FilterSelect
          label="Provider"
          multiple
          value={q.getList("provider")}
          onChange={v => q.set({ provider: v })}
          options={providers}
        />
        <FilterSelect
          label="OS"
          value={q.get("os")}
          onChange={v => q.set({ os: v })}
          options={[
            { value: "linux", label: "Linux" },
            { value: "windows", label: "Windows" },
          ]}
        />
        <FilterSelect
          label="Side"
          value={q.get("platform")}
          onChange={v => q.set({ platform: v })}
          options={[
            { value: "source", label: "Source" },
            { value: "destination", label: "Target" },
          ]}
          allLabel="Both"
        />
        {this.filtersActive ? <ClearFiltersButton onClick={() => this.clearFilters()} /> : null}
      </>
    );
  }

  rowActions(pool: MinionPool): DropdownAction[] {
    const withPool = (fn: () => void) => () => this.setState({ selectedMinionPools: [pool] }, fn);
    const allocated = pool.status === "ALLOCATED";
    return [
      { label: "Open", action: () => this.handleItemClick(pool) },
      { label: "Agent machines", action: () => this.props.onNavigate(`/minion-pools/${pool.id}/machines`) },
      {
        label: "Allocate",
        disabled: pool.status !== "DEALLOCATED",
        action: withPool(() => this.handleAllocate()),
      },
      {
        label: "Deallocate…",
        disabled: !(allocated || pool.status === "ERROR"),
        action: withPool(() => this.handleDeallocate()),
      },
      { label: "Refresh", disabled: !allocated, action: withPool(() => this.handleRefreshAction()) },
      {
        label: "Delete…",
        color: ThemePalette.alert,
        disabled: !(pool.status === "DEALLOCATED" || pool.status === "ERROR"),
        action: withPool(() => this.setState({ showDeletePoolsModal: true })),
      },
    ];
  }

  getMinionsThatCanBe(
    action: "allocated" | "deallocated" | "refreshed" | "deleted",
  ): MinionPool[] {
    const minions = this.state.selectedMinionPools;
    switch (action) {
      case "allocated":
        return minions.filter(minion => minion.status === "DEALLOCATED");
      case "deallocated":
        return minions.filter(
          minion => minion.status === "ALLOCATED" || minion.status === "ERROR",
        );
      case "refreshed":
        return minions.filter(minion => minion.status === "ALLOCATED");
      default:
        return minions.filter(
          minion =>
            minion.status === "DEALLOCATED" || minion.status === "ERROR",
        );
    }
  }

  getEndpoint(endpointId: string) {
    return endpointStore.endpoints.find(endpoint => endpoint.id === endpointId);
  }

  async pollData(showLoading: boolean) {
    if (this.state.modalIsOpen || this.stopPolling) {
      return;
    }

    await Promise.all([minionPoolStore.loadMinionPools({ showLoading })]);

    this.pollTimeout = window.setTimeout(() => {
      this.pollData(false);
    }, configLoader.config.requestPollTimeout);
  }

  deleteSelectedMinionPools() {
    const pools = this.getMinionsThatCanBe("deleted");
    pools.forEach(pool => {
      minionPoolStore.deleteMinionPool(pool.id);
    });
    this.setState({ showDeletePoolsModal: false });
  }

  handleProjectChange() {
    projectStore.getProjects();
    endpointStore.getEndpoints();
    minionPoolStore.loadMinionPools({ showLoading: true });
  }

  handleItemClick(item: MinionPool) {
    this.props.onNavigate(`/minion-pools/${item.id}`);
  }

  handleReloadButtonClick() {
    projectStore.getProjects();
    endpointStore.getEndpoints();
    minionPoolStore.loadMinionPools({ showLoading: true });
  }

  handleEmptyListButtonClick() {
    providerStore.loadProviders();
    endpointStore.getEndpoints({ showLoading: true });
    this.setState({ showChooseMinionEndpointModal: true, modalIsOpen: true });
  }

  handleCloseChooseMinionPoolEndpointModal() {
    this.setState(
      {
        showChooseMinionEndpointModal: false,
        modalIsOpen: false,
      },
      () => {
        this.pollData(false);
      },
    );
  }

  handleBackMinionPoolModal() {
    this.setState({
      showChooseMinionEndpointModal: true,
      showMinionPoolModal: false,
    });
  }

  handleCloseMinionPoolModalRequest() {
    this.setState(
      {
        showMinionPoolModal: false,
        modalIsOpen: false,
      },
      () => {
        this.pollData(false);
      },
    );
  }

  handleChooseMinionPoolSelectEndpoint(
    selectedMinionPoolEndpoint: Endpoint,
    platform: "source" | "destination",
  ) {
    this.setState({
      showChooseMinionEndpointModal: false,
      showMinionPoolModal: true,
      selectedMinionPoolEndpoint,
      selectedMinionPoolPlatform: platform,
    });
  }

  handleModalOpen() {
    this.setState({ modalIsOpen: true });
  }

  handleModalClose() {
    this.setState({ modalIsOpen: false }, () => {
      this.pollData(false);
    });
  }

  async handleAllocate() {
    const pools = this.getMinionsThatCanBe("allocated");

    const plural = pools.length === 1 ? "" : "s";
    notificationStore.alert(`Allocating SwiftIQ Agent${plural}…`);
    await Promise.all(
      pools.map(minionPool =>
        minionPoolStore.runAction(minionPool.id, "allocate"),
      ),
    );
    await minionPoolStore.loadMinionPools();
  }

  handleDeallocate() {
    this.setState({
      showDeallocateConfirmation: true,
    });
  }

  async handleDeallocateConfirmation(force: boolean) {
    this.setState({
      showDeallocateConfirmation: false,
    });
    const pools = this.getMinionsThatCanBe("deallocated");
    const plural = pools.length === 1 ? "" : "s";
    notificationStore.alert(`Deallocating SwiftIQ Agent${plural}…`);
    await Promise.all(
      pools.map(minionPool =>
        minionPoolStore.runAction(minionPool.id, "deallocate", { force }),
      ),
    );
    await minionPoolStore.loadMinionPools();
  }

  async handleRefreshAction() {
    const pools = this.getMinionsThatCanBe("refreshed");
    const plural = pools.length === 1 ? "" : "s";
    notificationStore.alert(`Refreshing SwiftIQ Agent${plural}…`);
    await Promise.all(
      pools.map(minionPool =>
        minionPoolStore.runAction(minionPool.id, "refresh"),
      ),
    );
    await minionPoolStore.loadMinionPools();
  }

  render() {
    const canBeAllocated = this.getMinionsThatCanBe("allocated").length > 0;
    const canBeDeallocated = this.getMinionsThatCanBe("deallocated").length > 0;
    const canBeRefreshed = this.getMinionsThatCanBe("refreshed").length > 0;
    const canBeDeleted = this.getMinionsThatCanBe("deleted").length > 0;

    const BulkActions: DropdownAction[] = [
      {
        label: "Allocate",
        color: ThemePalette.primary,
        action: () => {
          this.handleAllocate();
        },
        disabled: !canBeAllocated,
        title: !canBeAllocated ? "Only a deallocated SwiftIQ Agent can be allocated" : "",
      },
      {
        label: "Deallocate",
        action: () => {
          this.handleDeallocate();
        },
        disabled: !canBeDeallocated,
        title: !canBeDeallocated ? "Only an allocated SwiftIQ Agent can be deallocated" : "",
      },
      {
        label: "Refresh",
        action: () => {
          this.handleRefreshAction();
        },
        disabled: !canBeRefreshed,
        title: !canBeRefreshed ? "Only an allocated SwiftIQ Agent can be refreshed" : "",
      },
      {
        label: "Delete",
        color: ThemePalette.alert,
        action: () => {
          this.setState({ showDeletePoolsModal: true });
        },
        disabled: !canBeDeleted,
        title: !canBeDeleted ? "Deallocate the SwiftIQ Agent first" : "",
      },
    ];

    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="minion-pools" />}
          listComponent={
            <FilterList
              toolbar={this.renderToolbar()}
              filtersActive={this.filtersActive}
              onClearFilters={() => this.clearFilters()}
              columns={MINION_POOL_COLUMNS}
              selectionLabel="SwiftIQ Agent"
              countLabel={n => `${n} SwiftIQ Agent${n === 1 ? "" : "s"}`}
              loading={minionPoolStore.loadingMinionPools}
              items={this.filteredPools()}
              dropdownActions={BulkActions}
              largeDropdownActionItems
              onItemClick={item => {
                this.handleItemClick(item);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              onSelectedItemsChange={selectedMinionPools => {
                this.setState({ selectedMinionPools });
              }}
              renderItemComponent={options => (
                <MinionPoolListItem
                  {...options}
                  endpointType={id => {
                    const endpoint = this.getEndpoint(id);
                    if (endpoint) {
                      return endpoint.type;
                    }
                    if (endpointStore.loading) {
                      return "Loading...";
                    }
                    return "Not Found";
                  }}
                  endpointName={id => this.getEndpoint(id)?.name}
                  actions={this.rowActions(options.item)}
                />
              )}
              emptyListIcon={Bot}
              emptyListMessage="No SwiftIQ Agents in this project yet"
              emptyListExtraMessage="A SwiftIQ Agent keeps a set of agent machines ready on an endpoint, so transfers reuse them instead of creating and deleting helper machines on every run."
              emptyListButtonLabel="New SwiftIQ Agent"
              onEmptyListButtonClick={() => {
                this.handleEmptyListButtonClick();
              }}
            />
          }
          headerComponent={
            <PageHeader
              title="SwiftIQ Agents"
              subtitle="Helper machines SwiftIQ uses to read and write disks."
              onProjectChange={() => {
                this.handleProjectChange();
              }}
              onModalOpen={() => {
                this.handleModalOpen();
              }}
              onModalClose={() => {
                this.handleModalClose();
              }}
            />
          }
        />
        {this.state.showChooseMinionEndpointModal ? (
          <MinionEndpointModal
            providers={providerStore.providers}
            endpoints={endpointStore.endpoints}
            loading={providerStore.providersLoading || endpointStore.loading}
            onRequestClose={() => {
              this.handleCloseChooseMinionPoolEndpointModal();
            }}
            onSelectEndpoint={(endpoint, platform) => {
              this.handleChooseMinionPoolSelectEndpoint(endpoint, platform);
            }}
          />
        ) : null}
        {this.state.showMinionPoolModal ? (
          <Modal
            isOpen
            title={`New ${
              this.state.selectedMinionPoolPlatform === "source" ? "source" : "target"
            } SwiftIQ Agent`}
            onRequestClose={() => {
              this.handleCloseMinionPoolModalRequest();
            }}
          >
            <MinionPoolModal
              platform={this.state.selectedMinionPoolPlatform}
              cancelButtonText="Back"
              endpoint={this.state.selectedMinionPoolEndpoint!}
              onCancelClick={() => {
                this.handleBackMinionPoolModal();
              }}
              onRequestClose={() => {
                this.handleCloseMinionPoolModalRequest();
              }}
            />
          </Modal>
        ) : null}
        {this.state.showDeletePoolsModal ? (
          <AlertModal
            isOpen
            title="Delete SwiftIQ Agents?"
            message="Are you sure you want to delete the selected SwiftIQ Agents?"
            extraMessage="Deleting a SwiftIQ Agent is permanent."
            onConfirmation={() => {
              this.deleteSelectedMinionPools();
            }}
            onRequestClose={() => {
              this.setState({ showDeletePoolsModal: false });
            }}
          />
        ) : null}
        {this.state.showDeallocateConfirmation ? (
          <MinionPoolConfirmationModal
            onCancelClick={() => {
              this.setState({ showDeallocateConfirmation: false });
            }}
            onExecuteClick={force => {
              this.handleDeallocateConfirmation(force);
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function MinionPoolsPageWithNavigate() {
  const navigate = useNavigate();
  const query = useListQuery();

  return <MinionPoolsPage onNavigate={navigate} query={query} />;
}

export default MinionPoolsPageWithNavigate;
